import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { PencilLine, Upload } from "lucide-react";
import { ENTRY_KINDS, kindsForVehicle, kindsForFleet } from "@/lib/entryKinds";

/**
 * AddDataDialog — single primary entry point for ALL data input.
 *
 * Step 1: pick a Mode (Manual Entry vs Bulk Import).
 * Step 2: pick a Kind (trip / charging / fuel) — adaptively shown based on the
 *         selected vehicle's energy type, or the union of the fleet's types.
 *
 * The parent owns the actual creation dialogs (AddTripDialog, AddEnergyDialog,
 * BulkImportDialog). This component just routes the user's choice back through
 * `onPickAction({ mode, kind, vehicleId })`.
 */
const MODES = [
  {
    id: "manual",
    label: "Manual Entry",
    description: "Log a single record with full telemetry.",
    icon: PencilLine,
  },
  {
    id: "bulk",
    label: "Bulk Import",
    description: "Upload CSV or Excel with many records at once.",
    icon: Upload,
  },
];

export default function AddDataDialog({
  open,
  onOpenChange,
  vehicles,
  defaultVehicleId,
  onPickAction,
}) {
  const [mode, setMode] = useState(null);
  const [vehicleId, setVehicleId] = useState(defaultVehicleId || "all");

  useEffect(() => {
    if (open) {
      setMode(null);
      setVehicleId(defaultVehicleId || (vehicles.length === 1 ? vehicles[0].id : "all"));
    }
  }, [open, defaultVehicleId, vehicles]);

  const scopedVehicle = useMemo(
    () => (vehicleId !== "all" ? vehicles.find((v) => v.id === vehicleId) : null),
    [vehicleId, vehicles]
  );

  const availableKinds = useMemo(() => {
    if (scopedVehicle) return kindsForVehicle(scopedVehicle);
    return kindsForFleet(vehicles);
  }, [scopedVehicle, vehicles]);

  const handlePick = (kindId) => {
    const effectiveVehicleId =
      vehicleId !== "all"
        ? vehicleId
        : vehicles.find((v) => kindsForVehicle(v).includes(kindId))?.id || vehicles[0]?.id;
    onPickAction?.({
      mode,
      kind: kindId,
      vehicleId: effectiveVehicleId,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] sm:max-w-[560px] p-0 overflow-hidden"
        data-testid="add-data-dialog"
        aria-describedby={undefined}
      >
        <div className="px-6 pt-6 pb-3">
          <div className="label-tiny mb-1.5">Add Data</div>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">
            {mode ? `${MODES.find((m) => m.id === mode).label} — pick a type` : "What would you like to log?"}
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)] text-sm mt-1.5 leading-relaxed">
            {mode
              ? "Options reflect what your selected vehicle supports."
              : "Choose how you'd like to bring data in."}
          </DialogDescription>
        </div>

        {/* Vehicle scoping — affects which kinds are listed */}
        {vehicles.length > 0 && (
          <div className="px-6 pb-3">
            <Label className="label-tiny mb-1.5 block">For Vehicle</Label>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] h-9"
                data-testid="add-data-vehicle-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]">
                {vehicles.length > 1 && (
                  <SelectItem value="all" data-testid="add-data-vehicle-all">
                    All vehicles (auto-pick)
                  </SelectItem>
                )}
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id} data-testid={`add-data-vehicle-${v.id}`}>
                    {v.name}
                    {v.energy_type && (
                      <span className="text-[var(--text-muted)] ml-2 text-xs">· {v.energy_type}</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Step 1: Mode picker */}
        {!mode && (
          <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="add-data-mode-grid">
            {MODES.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  data-testid={`add-data-mode-${m.id}`}
                  className="group text-left bg-[var(--bg-elevated)] border border-[var(--border-subtle)] hover:border-[#E82127] p-4 transition-colors flex flex-col"
                  style={{ borderRadius: 4, minHeight: 132 }}
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-[var(--bg-emphasis)] border border-[var(--border-subtle)] group-hover:border-[#E82127]/40 transition-colors" style={{ borderRadius: 999 }}>
                    <Icon className="w-4 h-4 text-[var(--text-primary)] group-hover:text-[#E82127] transition-colors" strokeWidth={1.75} />
                  </div>
                  <div className="mt-3">
                    <div className="text-sm font-medium tracking-tight text-[var(--text-primary)]">{m.label}</div>
                    <p className="text-[12px] text-[var(--text-secondary)] leading-relaxed mt-1">{m.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Step 2: Kind picker (adaptive) */}
        {mode && (
          <div className="px-6 pb-6 grid grid-cols-1 gap-2.5" data-testid="add-data-kind-grid">
            {availableKinds.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)] py-6 text-center border border-dashed border-[var(--border-subtle)]" style={{ borderRadius: 2 }}>
                No vehicles to log against. Add a vehicle first.
              </div>
            ) : (
              availableKinds.map((kind) => {
                const meta = ENTRY_KINDS[kind];
                const Icon = meta.icon;
                return (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => handlePick(kind)}
                    data-testid={`add-data-kind-${kind}`}
                    className="group flex items-center gap-3 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] hover:border-white/30 px-4 py-3.5 text-left transition-colors"
                    style={{ borderRadius: 4 }}
                  >
                    <span
                      className="w-9 h-9 flex items-center justify-center bg-[var(--bg-emphasis)] border border-[var(--border-subtle)]"
                      style={{ borderRadius: 999 }}
                    >
                      <Icon className="w-4 h-4 text-[var(--text-primary)]" strokeWidth={1.75} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[var(--text-primary)] tracking-tight">{meta.label}</div>
                      <div className="text-[12px] text-[var(--text-secondary)] truncate">{meta.description}</div>
                    </div>
                    <span className="text-[var(--text-faint)] group-hover:text-[var(--text-primary)] text-sm transition-colors">→</span>
                  </button>
                );
              })
            )}
            <div className="mt-1">
              <button
                type="button"
                onClick={() => setMode(null)}
                className="tap text-[11px] tracking-wider uppercase text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                data-testid="add-data-back-btn"
              >
                ← Back
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
